import type { DesktopAccountSummary } from '@/lib/utils/tauri'
import { Fragment, useState } from 'react'
import { useAuth as useAuthContext } from '@/components/auth/AuthProvider'
import { AvatarWithUpload } from '@/components/ui/avatar-with-upload'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { UserAvatar } from '@/components/ui/user-avatar'
import { useNavigationItems } from '@/hooks/useNavigationItems'
import { useReleaseNotesUnread } from '@/hooks/useReleaseNotesUnread'
import { useRealtime } from '@/lib/realtime/core/useRealtime'
import { cn } from '@/lib/utils'
import {
  desktopSecureStorage,
  isTauri,
  openInNewWindow,
} from '@/lib/utils/tauri'
import { resetPersistedModuleFilters } from '@/store/filter-scope'
import { useKeyboardShortcutsStore } from '@/store/keyboard-shortcuts.store'
import { useOrganizationStore } from '@/store/organization.store'
import { handleLogout } from '@/utils/auth/auth-utils'
import {
  BookOpenTextIcon,
  CheckIcon,
  KeyboardIcon,
  PlusIcon,
  RocketLaunchIcon,
  SignOutIcon,
  UserSwitchIcon,
} from '@phosphor-icons/react'
import { Link, useNavigate } from 'react-router-dom'

interface UserMenuProps {
  getNavigationPath?: (path: string) => string
  onOpenChange?: (open: boolean) => void
  compact?: boolean
  /** Hide organization-specific menu items (e.g., when no org is selected) */
  hideOrgSettings?: boolean
  portalProfileHref?: string
  /**
   * Help Center + Keyboard shortcuts. Off for the client portal, which has
   * neither the /help route nor the shortcuts overlay mounted.
   */
  showHelpItems?: boolean
}

export function UserMenu({
  getNavigationPath: externalGetNavigationPath,
  onOpenChange,
  compact = false,
  hideOrgSettings = false,
  portalProfileHref,
  showHelpItems = true,
}: UserMenuProps) {
  const { user } = useAuthContext()
  const navigate = useNavigate()
  const { userMenuItems } = useNavigationItems()
  const { isConnected, isReady } = useRealtime({ suppressLogging: true })
  const connectionStatus = isConnected && isReady
  const { currentOrganization } = useOrganizationStore()
  const {
    hasUnread: hasUnreadReleaseNotes,
    markVisited: markReleaseNotesVisited,
  } = useReleaseNotesUnread(user?.id)
  const openShortcuts = useKeyboardShortcutsStore(state => state.openShortcuts)
  const [desktopAccounts, setDesktopAccounts] = useState<
    DesktopAccountSummary[]
  >([])
  const [activeAccountId, setActiveAccountId] = useState<string | null>(null)
  const visibleDesktopAccounts = desktopAccounts.filter(account =>
    Boolean(account.email),
  )

  const refreshDesktopAccounts = async () => {
    if (!isTauri()) return
    const [accounts, activeId] = await Promise.all([
      desktopSecureStorage.getAccounts(),
      desktopSecureStorage.getActiveAccountId(),
    ])
    setDesktopAccounts(accounts)
    setActiveAccountId(activeId)
  }

  const handleAccountSwitch = async (accountId: string) => {
    if (!isTauri() || accountId === activeAccountId) return
    const didSwitch = await desktopSecureStorage.setActiveAccount(accountId)
    if (didSwitch) {
      resetPersistedModuleFilters()
      localStorage.removeItem('currentOrganizationId')
      window.location.href = '/organizations'
    }
  }

  // Desktop opens the Help Center in its own window so it doesn't take over
  // the workspace the user was in; web just navigates.
  const handleOpenHelpCenter = async () => {
    if (isTauri()) {
      await openInNewWindow('/help', 'Help Center')
      return
    }
    navigate('/help')
  }

  const handleAddAccount = () => {
    window.location.href = '/login?addAccount=true'
  }

  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open)
    if (open) {
      refreshDesktopAccounts()
    }
  }

  // Internal path transformation that handles org-scoped routes
  const getNavigationPath = (path: string) => {
    // If external function is provided and returns something different, use it
    if (externalGetNavigationPath) {
      const externalPath = externalGetNavigationPath(path)
      if (externalPath !== path) {
        return externalPath
      }
    }

    // Global routes that don't need org prefix
    const globalRoutes = ['/organizations', '/profile']
    if (globalRoutes.includes(path)) {
      return path
    }

    // User settings routes are global, except integrations which is org-scoped
    if (
      path.startsWith('/settings') &&
      !path.startsWith('/settings/integrations')
    ) {
      return path
    }

    // Add org prefix for admin routes and org-scoped settings
    if (currentOrganization?.slug) {
      if (
        path.startsWith('/admin/') ||
        path.startsWith('/settings/integrations')
      ) {
        return `/${currentOrganization.slug}${path}`
      }
    }

    return path
  }

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            'group flex items-center justify-center rounded-lg border border-transparent transition-all duration-200 hover:border-primary/20 hover:bg-primary/10',
            compact ? 'h-6 w-6' : 'h-8 w-8',
          )}
          aria-label="User menu"
        >
          <div className="relative flex items-center justify-center">
            <AvatarWithUpload
              user={user}
              size="xs"
              className={compact ? 'h-5 w-5' : 'h-6 w-6'}
            />
            <span
              className={cn(
                'absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full shadow-sm ring-2 ring-background',
                connectionStatus ? 'bg-green-500' : 'bg-red-500',
              )}
              title={connectionStatus ? 'Connected' : 'Disconnected'}
            ></span>
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-64 rounded-lg border border-border/60 bg-background p-0 shadow-xl"
      >
        {/* Show user info header only in web (non-Tauri) since desktop has accounts list */}
        {!isTauri() && (
          <div className="flex items-center gap-3 rounded-t-lg border-b border-border/30 bg-muted/20 p-3">
            <AvatarWithUpload
              user={user}
              size="md"
              className="h-10 w-10 flex-shrink-0"
            />
            <div className="flex min-w-0 flex-1 flex-col">
              <p className="truncate text-sm font-bold leading-none text-foreground">
                {user?.displayName || user?.firstName
                  ? `${user?.displayName || `${user?.firstName || ''} ${user?.lastName || ''}`}`.trim()
                  : 'User'}
              </p>
              <div className="mt-1.5 flex items-center">
                <div
                  className={`h-1.5 w-1.5 rounded-full ${connectionStatus ? 'bg-green-500' : 'bg-red-500'} mr-1.5 flex-shrink-0 shadow-sm`}
                ></div>
                <p className="text-xs font-medium text-muted-foreground">
                  {connectionStatus ? 'Online' : 'Offline'}
                </p>
              </div>
              <p className="mt-1 truncate text-xs text-muted-foreground">
                {user?.email}
              </p>
            </div>
          </div>
        )}
        <div className="p-1">
          {isTauri() && (
            <>
              {visibleDesktopAccounts.length > 0 && (
                <>
                  <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Accounts
                  </div>
                  {visibleDesktopAccounts.map(account => {
                    const isActive = account.id === activeAccountId
                    const accountUserId =
                      account.userId ||
                      (!account.id.startsWith('email:') &&
                      !account.id.startsWith('unknown:')
                        ? account.id
                        : undefined)
                    const accountLabel =
                      account.displayName ||
                      [account.firstName, account.lastName]
                        .filter(Boolean)
                        .join(' ') ||
                      account.email ||
                      'Account'
                    return (
                      <DropdownMenuItem
                        key={account.id}
                        onClick={() => handleAccountSwitch(account.id)}
                        className={cn(
                          'transition-tonal group cursor-pointer rounded-lg p-0 duration-200 hover:bg-primary/10',
                          isActive && 'bg-primary/10',
                        )}
                      >
                        <div className="flex w-full items-center gap-2 px-2 py-2">
                          <UserAvatar
                            user={{
                              id: accountUserId,
                              displayName: account.displayName || undefined,
                              firstName: account.firstName || undefined,
                              lastName: account.lastName || undefined,
                              email: account.email || undefined,
                            }}
                            size="xs"
                            className="h-5 w-5"
                          />
                          <div className="flex min-w-0 flex-1 flex-col">
                            <span className="truncate text-sm font-medium text-foreground">
                              {accountLabel}
                            </span>
                            {account.email && (
                              <span className="truncate text-xs text-muted-foreground">
                                {account.email}
                              </span>
                            )}
                          </div>
                          {isActive && (
                            <CheckIcon className="h-3.5 w-3.5 text-primary" />
                          )}
                        </div>
                      </DropdownMenuItem>
                    )
                  })}
                </>
              )}
              <DropdownMenuItem
                onClick={handleAddAccount}
                className="transition-tonal group cursor-pointer rounded-lg p-0 duration-200 hover:bg-primary/10"
              >
                <div className="flex w-full items-center gap-2 px-2 py-2">
                  <div className="transition-tonal flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/10 duration-200 group-hover:bg-primary/15">
                    <PlusIcon className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <span className="flex-1 text-sm font-medium text-foreground transition-colors group-hover:text-primary">
                    Add account
                  </span>
                  <UserSwitchIcon className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
              </DropdownMenuItem>
              <div className="mx-2 my-1 h-px bg-border/50" />
            </>
          )}
          {userMenuItems.map(item => {
            // /user-settings is org-scoped. Resolve it per context: the client
            // portal has its own settings page, an org routes to its
            // user-settings page, and everywhere else (Organizations chooser,
            // portal access chooser) falls back to the org-less /account page
            // so the global account fields stay reachable.
            const isUserSettings = item.href === '/user-settings'
            const shouldHideOrgSettings =
              hideOrgSettings || !currentOrganization?.slug

            let itemHref: string
            if (isUserSettings && portalProfileHref) {
              itemHref = portalProfileHref
            } else if (isUserSettings && !shouldHideOrgSettings) {
              itemHref = `/${currentOrganization?.slug}/user-settings`
            } else if (isUserSettings) {
              itemHref = '/account'
            } else {
              itemHref = getNavigationPath(item.href)
            }

            return (
              <Fragment key={item.href}>
                {item.divider && (
                  <div className="mx-2 my-1 h-px bg-border/50" />
                )}
                <DropdownMenuItem
                  asChild
                  className="transition-tonal group cursor-pointer rounded-lg p-0 duration-200 hover:bg-primary/10 focus:bg-primary/10"
                >
                  <Link
                    to={itemHref}
                    className="flex w-full items-center gap-2 px-2 py-2"
                  >
                    <div className="transition-tonal flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/10 duration-200 group-hover:bg-primary/15">
                      <item.icon className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <span className="flex-1 truncate text-sm font-medium text-foreground transition-colors group-hover:text-primary">
                      {item.label}
                    </span>
                  </Link>
                </DropdownMenuItem>
              </Fragment>
            )
          })}
          {showHelpItems && (
            <>
              <div className="mx-2 my-1 h-px bg-border/50" />
              <DropdownMenuItem
                onClick={handleOpenHelpCenter}
                className="transition-tonal group cursor-pointer rounded-lg p-0 duration-200 hover:bg-primary/10 focus:bg-primary/10"
              >
                <div className="flex w-full items-center gap-2 px-2 py-2">
                  <div className="transition-tonal flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/10 duration-200 group-hover:bg-primary/15">
                    <BookOpenTextIcon className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <span className="flex-1 truncate text-sm font-medium text-foreground transition-colors group-hover:text-primary">
                    Help Center
                  </span>
                </div>
              </DropdownMenuItem>
              {/* Sits next to Help Center because it answers the same kind of
                  question. The overlay is also reachable by pressing "?". */}
              <DropdownMenuItem
                onClick={() => openShortcuts()}
                className="transition-tonal group cursor-pointer rounded-lg p-0 duration-200 hover:bg-primary/10 focus:bg-primary/10"
              >
                <div className="flex w-full items-center gap-2 px-2 py-2">
                  <div className="transition-tonal flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/10 duration-200 group-hover:bg-primary/15">
                    <KeyboardIcon className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <span className="flex-1 truncate text-sm font-medium text-foreground transition-colors group-hover:text-primary">
                    Keyboard shortcuts
                  </span>
                  <kbd className="rounded border border-border/60 bg-muted px-1.5 py-0.5 font-sans text-[10px] font-semibold text-muted-foreground">
                    ?
                  </kbd>
                </div>
              </DropdownMenuItem>
            </>
          )}
          <div className="mx-2 my-1 h-px bg-border/50" />
          <DropdownMenuItem
            asChild
            className="transition-tonal group cursor-pointer rounded-lg p-0 duration-200 hover:bg-primary/10 focus:bg-primary/10"
          >
            <Link
              to="/whats-new"
              onClick={markReleaseNotesVisited}
              className="flex w-full items-center gap-2 px-2 py-2"
            >
              <div className="transition-tonal flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg border border-amber-500/20 bg-amber-500/10 duration-200 group-hover:bg-amber-500/15">
                <RocketLaunchIcon
                  className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400"
                  weight="duotone"
                />
              </div>
              <span className="flex-1 truncate text-sm font-medium text-foreground transition-colors group-hover:text-primary">
                What&apos;s New
              </span>
              {hasUnreadReleaseNotes ? (
                <>
                  <span
                    aria-hidden
                    className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--pickled-shell))] shadow-[0_0_0_3px_hsl(var(--pickled-shell)/0.12)]"
                  />
                  <span className="sr-only">New release notes available</span>
                </>
              ) : null}
            </Link>
          </DropdownMenuItem>
          <div className="mx-2 my-1 h-px bg-border/50" />
          <DropdownMenuItem
            onClick={() => handleLogout()}
            className="transition-tonal group cursor-pointer rounded-lg p-0 text-destructive duration-200 hover:bg-destructive/10 focus:bg-destructive/10 focus:text-destructive"
          >
            <div className="flex w-full items-center gap-2 px-2 py-2">
              <div className="transition-tonal flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg border border-destructive/20 bg-destructive/10 duration-200 group-hover:bg-destructive/15">
                <SignOutIcon className="h-3.5 w-3.5 text-destructive" />
              </div>
              <span className="flex-1 text-sm font-medium">Sign out</span>
            </div>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
